"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import { useLanguage } from "@/context/language-context"

interface ModernDatePickerProps {
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  autoOpen?: boolean
  portalContainer?: HTMLElement | null
  highZIndex?: boolean
}

const MONTHS = {
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ],
  fr: [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ]
}

const DAYS = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  fr: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"]
}

export function ModernDatePicker({ value, onChange, placeholder = "selectDate", disabled = false, autoOpen = false, portalContainer = null, highZIndex = false }: ModernDatePickerProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(autoOpen)
  const [viewMode, setViewMode] = useState<'days' | 'months' | 'years'>('days')
  const [currentDate, setCurrentDate] = useState(new Date())
  
  // Parse date string as UTC date
  const selectedDate = value ? parseLocalDate(value) : null

  // Helper function to parse date string as UTC date
  function parseLocalDate(dateString: string): Date {
    if (!dateString) return new Date()
    
    // Split the date string to get just the date part
    const [year, month, day] = dateString.split('-').map(Number)
    
    // Create date preserving the exact date values
    const date = new Date()
    date.setUTCFullYear(year)
    date.setUTCMonth(month - 1) // Month is 0-indexed
    date.setUTCDate(day)
    date.setUTCHours(0, 0, 0, 0)
    return date
  }

  // Helper function to create UTC-midnight date for today
  function getTodayUtc(): Date {
    const now = new Date()
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  }

  // Initialize current date to selected date or today - use value directly to avoid infinite loop
  useEffect(() => {
    if (value) {
      setCurrentDate(parseLocalDate(value))
    }
  }, [value])

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true)
    }
  }, [autoOpen])

  const handleDateSelect = (date: Date) => {
    if (!date) {
      onChange(null)
      setIsOpen(false)
      setViewMode('days')
      return
    }

    // Format the date as YYYY-MM-DD using UTC
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const formattedDate = `${year}-${month}-${day}`
    
    onChange(formattedDate)
    setIsOpen(false)
    setViewMode('days')
  }

  const handleClear = () => {
    onChange(null)
    setIsOpen(false)
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDay.getUTCDate();
    const startingDayOfWeek = firstDay.getUTCDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month (in UTC)
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(Date.UTC(year, month, day)));
    }

    return days;
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const year = prev.getUTCFullYear()
      const month = prev.getUTCMonth() + (direction === 'prev' ? -1 : 1)
      return new Date(Date.UTC(year, month, 1))
    })
  }

  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const year = prev.getUTCFullYear() + (direction === 'prev' ? -1 : 1)
      const month = prev.getUTCMonth()
      return new Date(Date.UTC(year, month, 1))
    })
  }

  const selectMonth = (monthIndex: number) => {
    setCurrentDate(prev => {
      const year = prev.getUTCFullYear()
      return new Date(Date.UTC(year, monthIndex, 1))
    })
    setViewMode('days')
  }

  const selectYear = (year: number) => {
    setCurrentDate(prev => {
      const month = prev.getUTCMonth()
      return new Date(Date.UTC(year, month, 1))
    })
    setViewMode('months')
  }

  const getYearRange = () => {
    const currentYear = currentDate.getUTCFullYear()
    const startYear = Math.floor(currentYear / 10) * 10
    return Array.from({ length: 12 }, (_, i) => startYear + i)
  }

  const isToday = (date: Date) => {
    const todayUtc = getTodayUtc()
    return date.getUTCFullYear() === todayUtc.getUTCFullYear() &&
           date.getUTCMonth() === todayUtc.getUTCMonth() &&
           date.getUTCDate() === todayUtc.getUTCDate()
  }

  const isSelected = (date: Date) => {
    return selectedDate && 
           date.getUTCFullYear() === selectedDate.getUTCFullYear() &&
           date.getUTCMonth() === selectedDate.getUTCMonth() &&
           date.getUTCDate() === selectedDate.getUTCDate()
  }

  const formattedDate = selectedDate ? 
    `${String(selectedDate.getUTCDate()).padStart(2, '0')} ${MONTHS[language][selectedDate.getUTCMonth()]} ${selectedDate.getUTCFullYear()}` : 
    placeholder

  const renderDaysView = () => {
    const days = getDaysInMonth(currentDate)
    
    return (
      <div className="p-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth('prev')}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => setViewMode('months')}
              className="text-sm font-medium hover:bg-gray-100"
            >
              {MONTHS[language][currentDate.getUTCMonth()]}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setViewMode('years')}
              className="text-sm font-medium hover:bg-gray-100"
            >
              {currentDate.getUTCFullYear()}
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth('next')}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 mb-2">
          {DAYS[language].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, index) => (
            <div key={index} className="aspect-square">
              {date && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDateSelect(date)}
                  className={`w-full h-full text-sm ${
                    isSelected(date)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : isToday(date)
                      ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {date.getUTCDate()}
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-between mt-4 pt-3 border-t">
          <Button variant="ghost" size="sm" onClick={handleClear}>
            {t('clearAll')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDateSelect(getTodayUtc())}>
            {t('today')}
          </Button>
        </div>
      </div>
    )
  }

  const renderMonthsView = () => (
    <div className="p-3">
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateYear('prev')}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          onClick={() => setViewMode('years')}
          className="text-sm font-medium hover:bg-gray-100"
        >
          {currentDate.getUTCFullYear()}
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateYear('next')}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {MONTHS[language].map((month, index) => (
          <Button
            key={month}
            variant="ghost"
            onClick={() => selectMonth(index)}
            className={`text-sm py-3 ${
              currentDate.getUTCMonth() === index
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            {month.slice(0, 3)}
          </Button>
        ))}
      </div>
    </div>
  )

  const renderYearsView = () => {
    const years = getYearRange()
    
    return (
      <div className="p-3">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCurrentDate(prev => {
                const newDate = new Date(prev)
                newDate.setUTCFullYear(newDate.getUTCFullYear() - 12)
                return newDate
              })
            }}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <span className="text-sm font-medium">
            {years[0]} - {years[years.length - 1]}
          </span>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCurrentDate(prev => {
                const newDate = new Date(prev)
                newDate.setUTCFullYear(newDate.getUTCFullYear() + 12)
                return newDate
              })
            }}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {years.map(year => (
            <Button
              key={year}
              variant="ghost"
              onClick={() => selectYear(year)}
              className={`text-sm py-3 ${
                currentDate.getUTCFullYear() === year
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              {year}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={`w-full justify-start text-left font-normal ${
            !selectedDate ? "text-muted-foreground" : ""
          } border border-input`}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? formattedDate : <span className="text-gray-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" portalContainer={portalContainer} highZIndex={highZIndex}>
        {viewMode === 'days' && renderDaysView()}
        {viewMode === 'months' && renderMonthsView()}
        {viewMode === 'years' && renderYearsView()}
      </PopoverContent>
    </Popover>
  )
} 